import { useConfirmDialog } from '../hooks/useConfirmDialog';
import React, { useEffect, useState } from 'react';
import { getErrorMessage } from '@/lib/utils';
import { useI18n } from '../contexts/I18nContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  FolderOpen,
  Plus,
  Pencil,
  Trash2,
  Package,
  MoreVertical,
  Eye,
  EyeOff,
  Star,
  Search,
  X,
  Check
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CatalogsPage = () => {
  const { confirm: confirmAction, ConfirmDialog } = useConfirmDialog();
  const { t } = useI18n();
  const [catalogs, setCatalogs] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [showNewCatalogDialog, setShowNewCatalogDialog] = useState(false);
  const [showEditCatalogDialog, setShowEditCatalogDialog] = useState(false);
  const [showAddProductsDialog, setShowAddProductsDialog] = useState(false);
  const [catalogFormData, setCatalogFormData] = useState({
    name: '',
    description: '',
    is_visible: true
  });
  const [saving, setSaving] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catalogsRes, productsRes] = await Promise.all([
        axios.get(`${API}/catalogs`),
        axios.get(`${API}/products`)
      ]);
      setCatalogs(catalogsRes.data);
      setProducts(productsRes.data);
      
      // Seleziona il primo catalogo o quello predefinito
      const defaultCatalog = catalogsRes.data.find(c => c.is_default) || catalogsRes.data[0];
      if (defaultCatalog) setSelectedCatalog(defaultCatalog);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Prodotti nel catalogo selezionato
  const catalogProducts = selectedCatalog 
    ? products.filter(p => selectedCatalog.product_ids?.includes(p.id))
    : [];

  // Prodotti disponibili (non ancora nel catalogo)
  const availableProducts = selectedCatalog
    ? products.filter(p => !selectedCatalog.product_ids?.includes(p.id))
    : products;

  // Filtra prodotti disponibili per ricerca
  const filteredAvailableProducts = availableProducts.filter(p =>
    p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.sku?.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.category?.toLowerCase().includes(productSearch.toLowerCase())
  );

  const handleCreateCatalog = async () => {
    if (!catalogFormData.name.trim()) {
      toast.error('Inserisci un nome per il catalogo');
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(`${API}/catalogs`, {
        name: catalogFormData.name.trim(),
        description: catalogFormData.description.trim(),
        is_visible: catalogFormData.is_visible,
        product_ids: []
      });
      toast.success(t('catalog.catalogCreated'));
      setShowNewCatalogDialog(false);
      setCatalogFormData({ name: '', description: '', is_visible: true });
      fetchData();
      setSelectedCatalog(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCatalog = async () => {
    if (!catalogFormData.name.trim()) {
      toast.error(t('common.requiredField'));
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/catalogs/${selectedCatalog.id}`, {
        name: catalogFormData.name.trim(),
        description: catalogFormData.description.trim(),
        is_visible: catalogFormData.is_visible,
        product_ids: selectedCatalog.product_ids || []
      });
      toast.success(t('catalog.catalogUpdated'));
      setShowEditCatalogDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.errorSaving')));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCatalog = async (catalogId) => {
    const catalog = catalogs.find(c => c.id === catalogId);
    if (catalog?.is_default) {
      toast.error(t('catalog.cannotDeleteDefault'));
      return;
    }
    
    if (!await confirmAction({ title: 'Elimina catalogo', description: 'Sei sicuro di voler eliminare questo catalogo?', confirmText: 'Elimina', variant: 'destructive' })) return;

    try {
      await axios.delete(`${API}/catalogs/${catalogId}`);
      toast.success(t('catalog.catalogDeleted'));
      if (selectedCatalog?.id === catalogId) {
        const defaultCatalog = catalogs.find(c => c.is_default);
        setSelectedCatalog(defaultCatalog || null);
      }
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.errorDeleting')));
    }
  };

  const handleAddProducts = async () => {
    if (selectedProductIds.length === 0) {
      toast.error('Seleziona almeno un prodotto');
      return;
    }

    setSaving(true);
    try {
      // Aggiungi ogni prodotto al catalogo
      for (const productId of selectedProductIds) {
        await axios.post(`${API}/catalogs/${selectedCatalog.id}/products/${productId}`);
      }
      toast.success(`${selectedProductIds.length} prodotti aggiunti al catalogo`);
      setShowAddProductsDialog(false);
      setSelectedProductIds([]);
      setProductSearch('');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'aggiunta"));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveProduct = async (productId) => {
    if (!await confirmAction({ title: 'Rimuovi prodotto', description: 'Rimuovere questo prodotto dal catalogo?', confirmText: 'Rimuovi', variant: 'destructive' })) return;

    try {
      await axios.delete(`${API}/catalogs/${selectedCatalog.id}/products/${productId}`);
      toast.success('Prodotto rimosso dal catalogo');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella rimozione'));
    }
  };

  const openEditDialog = () => {
    setCatalogFormData({
      name: selectedCatalog?.name || '',
      description: selectedCatalog?.description || '',
      is_visible: selectedCatalog?.is_visible ?? true
    });
    setShowEditCatalogDialog(true);
  };

  const toggleProductSelection = (productId) => {
    setSelectedProductIds(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="catalogs-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold">Cataloghi</h1>
        <p className="text-muted-foreground mt-1">
          Organizza i tuoi prodotti in collezioni
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar - Lista Cataloghi */}
        <Card className="lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-heading text-lg">I tuoi cataloghi</CardTitle>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => {
                setCatalogFormData({ name: '', description: '', is_visible: true });
                setShowNewCatalogDialog(true);
              }}
              data-testid="new-catalog-btn"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {catalogs.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nessun catalogo</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => setShowNewCatalogDialog(true)}
                >
                  Crea il primo catalogo
                </Button>
              </div>
            ) : (
              catalogs.map((catalog) => (
                <div
                  key={catalog.id}
                  className={`group relative p-3 rounded-lg transition-colors cursor-pointer ${
                    selectedCatalog?.id === catalog.id 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => setSelectedCatalog(catalog)}
                  data-testid={`catalog-${catalog.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {catalog.is_default && <Star className="h-4 w-4 shrink-0" />}
                      {!catalog.is_visible && <EyeOff className="h-4 w-4 shrink-0 opacity-50" />}
                      <span className="font-medium truncate">{catalog.name}</span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className={`h-8 w-8 opacity-0 group-hover:opacity-100 ${
                            selectedCatalog?.id === catalog.id ? 'text-primary-foreground hover:bg-primary-foreground/20' : ''
                          }`}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCatalog(catalog);
                          setCatalogFormData({
                            name: catalog.name,
                            description: catalog.description || '',
                            is_visible: catalog.is_visible
                          });
                          setShowEditCatalogDialog(true);
                        }}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Modifica
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {!catalog.is_default && (
                          <DropdownMenuItem 
                            className="text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCatalog(catalog.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Elimina
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <span className={`text-xs ${selectedCatalog?.id === catalog.id ? 'opacity-70' : 'text-muted-foreground'}`}>
                    {catalog.product_ids?.length || 0} prodotti
                  </span>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Area principale - Prodotti nel catalogo */}
        <Card className="lg:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                {selectedCatalog?.is_default && <Star className="h-5 w-5 text-amber-500" />}
                {selectedCatalog?.name || 'Seleziona un catalogo'}
                {selectedCatalog && !selectedCatalog.is_visible && (
                  <Badge variant="secondary" className="ml-2">
                    <EyeOff className="h-3 w-3 mr-1" />
                    Nascosto
                  </Badge>
                )}
              </CardTitle>
              {selectedCatalog?.description && (
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedCatalog.description}
                </p>
              )}
            </div>
            {selectedCatalog && (
              <Button 
                onClick={() => {
                  setSelectedProductIds([]);
                  setProductSearch('');
                  setShowAddProductsDialog(true);
                }} 
                data-testid="add-products-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                Aggiungi Prodotti
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {catalogProducts.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="uppercase text-xs">Prodotto</TableHead>
                    <TableHead className="uppercase text-xs">SKU</TableHead>
                    <TableHead className="uppercase text-xs">Categoria</TableHead>
                    <TableHead className="uppercase text-xs">Unità</TableHead>
                    <TableHead className="uppercase text-xs text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {catalogProducts.map((product) => (
                    <TableRow key={product.id} data-testid={`catalog-product-${product.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {product.image_url ? (
                            <img 
                              src={product.image_url} 
                              alt={product.name}
                              className="w-10 h-10 rounded object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                              <Package className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                          <span className="font-medium">{product.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {product.sku || '-'}
                      </TableCell>
                      <TableCell>
                        {product.category && (
                          <Badge variant="outline">{product.category}</Badge>
                        )}
                      </TableCell>
                      <TableCell>{product.unit_of_measure}</TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleRemoveProduct(product.id)}
                          data-testid={`remove-product-${product.id}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nessun prodotto in questo catalogo</p>
                <p className="text-sm mt-1">Aggiungi prodotti per popolare il catalogo</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog Nuovo Catalogo */}
      <Dialog open={showNewCatalogDialog} onOpenChange={setShowNewCatalogDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              Nuovo Catalogo
            </DialogTitle>
            <DialogDescription>
              Crea una nuova collezione per organizzare i tuoi prodotti.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="catalog-name">Nome Catalogo *</Label>
              <Input
                id="catalog-name"
                value={catalogFormData.name}
                onChange={(e) => setCatalogFormData({ ...catalogFormData, name: e.target.value })}
                placeholder="es. Vini Rossi, Formaggi Stagionati, Prodotti Bio..."
                data-testid="catalog-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="catalog-description">Descrizione</Label>
              <Input
                id="catalog-description"
                value={catalogFormData.description}
                onChange={(e) => setCatalogFormData({ ...catalogFormData, description: e.target.value })}
                placeholder="Descrizione opzionale del catalogo"
                data-testid="catalog-description-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="catalog-visible"
                checked={catalogFormData.is_visible}
                onCheckedChange={(checked) => setCatalogFormData({ ...catalogFormData, is_visible: Boolean(checked) })}
              />
              <Label htmlFor="catalog-visible" className="cursor-pointer">
                Visibile ai clienti
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewCatalogDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleCreateCatalog}
              disabled={saving || !catalogFormData.name.trim()}
              data-testid="create-catalog-btn"
            >
              {saving ? t('common.loading') : 'Crea Catalogo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica Catalogo */}
      <Dialog open={showEditCatalogDialog} onOpenChange={setShowEditCatalogDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Modifica Catalogo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-catalog-name">Nome Catalogo *</Label>
              <Input
                id="edit-catalog-name"
                value={catalogFormData.name}
                onChange={(e) => setCatalogFormData({ ...catalogFormData, name: e.target.value })}
                data-testid="edit-catalog-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-catalog-description">Descrizione</Label>
              <Input
                id="edit-catalog-description"
                value={catalogFormData.description}
                onChange={(e) => setCatalogFormData({ ...catalogFormData, description: e.target.value })}
                data-testid="edit-catalog-description-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-catalog-visible"
                checked={catalogFormData.is_visible}
                onCheckedChange={(checked) => setCatalogFormData({ ...catalogFormData, is_visible: Boolean(checked) })}
              />
              <Label htmlFor="edit-catalog-visible" className="cursor-pointer">
                Visibile ai clienti
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditCatalogDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleUpdateCatalog}
              disabled={saving || !catalogFormData.name.trim()}
              data-testid="save-catalog-btn"
            >
              {saving ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Aggiungi Prodotti */}
      <Dialog open={showAddProductsDialog} onOpenChange={setShowAddProductsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Aggiungi Prodotti a "{selectedCatalog?.name}"
            </DialogTitle>
            <DialogDescription>
              Seleziona i prodotti da aggiungere a questo catalogo.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-10"
                placeholder="Cerca prodotti..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                data-testid="product-search-input"
              />
            </div>

            {/* Products list */}
            <div className="max-h-[400px] overflow-y-auto border rounded-lg">
              {filteredAvailableProducts.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Prodotto</TableHead>
                      <TableHead>Categoria</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAvailableProducts.map((product) => (
                      <TableRow 
                        key={product.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleProductSelection(product.id)}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedProductIds.includes(product.id)}
                            onCheckedChange={() => toggleProductSelection(product.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {product.image_url ? (
                              <img 
                                src={product.image_url} 
                                alt={product.name}
                                className="w-8 h-8 rounded object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                                <Package className="h-4 w-4 text-muted-foreground" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{product.name}</p>
                              {product.sku && (
                                <p className="text-xs text-muted-foreground">{product.sku}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {product.category && (
                            <Badge variant="outline">{product.category}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nessun prodotto disponibile</p>
                  <p className="text-sm">Tutti i prodotti sono già in questo catalogo</p>
                </div>
              )}
            </div>

            {selectedProductIds.length > 0 && (
              <div className="mt-4 p-3 bg-muted rounded-lg flex items-center justify-between">
                <span className="text-sm">
                  <strong>{selectedProductIds.length}</strong> prodotti selezionati
                </span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedProductIds([])}
                >
                  Deseleziona tutti
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddProductsDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleAddProducts}
              disabled={saving || selectedProductIds.length === 0}
              data-testid="confirm-add-products-btn"
            >
              {saving ? t('common.loading') : `Aggiungi ${selectedProductIds.length} Prodotti`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CatalogsPage;
